import { DOMNode, Element, htmlToDOM, Text } from "html-react-parser";

/**
 * Replaces the last space in a headline with a non-breaking space so the final word never wraps onto a line by itself.
 */
export const preventOrphan = (text: string | undefined | null): string => {
    if (!text) {
        return "";
    }

    const trimmed = text.trim();
    const lastSpace = trimmed.lastIndexOf(" ");
    if (lastSpace <= 0) {
        return trimmed;
    }

    return `${trimmed.slice(0, lastSpace)} ${trimmed.slice(lastSpace + 1)}`;
};

/** Length of the visible text (including spaces) in a block of HTML. */
export const getPlainTextLength = (html: string | undefined | null): number => {
    if (!html) {
        return 0;
    }

    return html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&[a-z]+;|&#\d+;/gi, "x").length;
};

/**
 * Cuts HTML down to roughly `maxChars` of visible text (spaces included) on a word boundary while keeping the markup
 * intact. No ellipsis is appended; the copy simply stops. The returned nodes can be rendered with `domToReact`.
 */
export const truncateHtml = (html: string, maxChars: number): DOMNode[] => {
    const nodes = htmlToDOM(html) as DOMNode[];
    let remaining = maxChars;

    const truncateNodes = (list: DOMNode[]): DOMNode[] => {
        const result: DOMNode[] = [];

        for (const node of list) {
            if (remaining <= 0) {
                break;
            }

            if (node.type === "text") {
                const textNode = node as Text;
                const text = textNode.data ?? "";

                if (text.length <= remaining) {
                    remaining -= text.length;
                    result.push(node);
                    continue;
                }

                let cut = text.slice(0, remaining);
                const lastSpace = cut.lastIndexOf(" ");
                if (lastSpace > 0) {
                    cut = cut.slice(0, lastSpace);
                }
                textNode.data = cut.replace(/[\s,;:\-–—]+$/, "");
                remaining = 0;
                result.push(node);
                continue;
            }

            if ("children" in node && Array.isArray((node as Element).children)) {
                const element = node as Element;
                element.children = truncateNodes(element.children as DOMNode[]) as Element["children"];
                if (element.children.length > 0 || !["p", "ul", "ol", "li", "div"].includes(element.name)) {
                    result.push(node);
                }
                continue;
            }

            result.push(node);
        }

        return result;
    };

    return truncateNodes(nodes);
};
